import { ChangeDetectionStrategy, Component, inject, signal, computed, OnInit } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { EventsService, Event } from "@shared/services/events.service";
import { rxResource } from "@angular/core/rxjs-interop";
import { Router, RouterModule } from "@angular/router";
import { AuthService } from "@shared/services/auth.service";

import { UiEventCardComponent } from "../shared/components/ui-event-card/ui-event-card";
import { UiTitleBlockComponent } from "../shared/components/ui-title-block/ui-title-block";

export interface HomeEvent extends Event {
  isRegistered: boolean;
  isUserParticipant: boolean;
  additionalParticipantsCount: number;
  confirmedParticipants: number;
  confirmedSupporters: number;
  pendingParticipants: number;
  pendingSupporters: number;
  cancelledParticipants: number;
  cancelledSupporters: number;
  displayStatus: string | null;
}

@Component({
  selector: "app-home",
  standalone: true,
  imports: [CommonModule, RouterModule, DatePipe, UiEventCardComponent, UiTitleBlockComponent],
  templateUrl: "./home.html",
  styleUrl: "./home.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent {
  private readonly eventsService = inject(EventsService);
  protected readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly isAuthenticated = this.authService.isAuthenticated;

  protected readonly eventsResource = rxResource<Event[], void>({
    stream: () => this.eventsService.getUpcomingEvents(),
  });

  protected readonly events = computed<HomeEvent[]>(() => {
    const rawEvents = this.eventsResource.value() || [];
    const u = this.authService.currentUser();
    if (!u || !u.participants) {
      return rawEvents.map(event => ({
        ...event,
        isRegistered: false,
        isUserParticipant: false,
        additionalParticipantsCount: 0,
        confirmedParticipants: 0,
        confirmedSupporters: 0,
        pendingParticipants: 0,
        pendingSupporters: 0,
        cancelledParticipants: 0,
        cancelledSupporters: 0,
        displayStatus: null,
      }));
    }

    return rawEvents.map(event => {
      let isRegistered = false;
      let isUserParticipant = false;
      let additionalParticipantsCount = 0;
      let confirmedParticipants = 0;
      let confirmedSupporters = 0;
      let pendingParticipants = 0;
      let pendingSupporters = 0;
      let cancelledParticipants = 0;
      let cancelledSupporters = 0;
      const allStatuses = new Set<string>();

      u.participants?.forEach(p => {
        // Check for event information in participant itself (added to serializer)
        if (p.event_id === event.id) {
          isRegistered = true;
          allStatuses.add(p.status);

          const isConfirmed = p.status === 'c' || p.status === 'v' || p.status === 'p';
          const isCancelled = p.status === 'x';

          if (isConfirmed) {
            confirmedParticipants++;
          } else if (isCancelled) {
            cancelledParticipants++;
          } else {
            pendingParticipants++;
          }

          p.supporters?.forEach((s: any) => {
            const isSConfirmed = s.status === 'c' || s.status === 'v' || s.status === 'p';
            const isSCancelled = s.status === 'x';
            if (isSConfirmed) {
              confirmedSupporters++;
            } else if (isSCancelled) {
              cancelledSupporters++;
            } else {
              pendingSupporters++;
            }
          });

          if (p.is_self) {
            isUserParticipant = true;
          } else if (p.type !== 's') { // Not a supporter
            additionalParticipantsCount++;
          }
        }
      });

      let displayStatus: string | null = null;
      if (isRegistered) {
        const statusList = Array.from(allStatuses) as string[];
        displayStatus = "Plata în așteptare";
        if (statusList.includes("prezent")) {
          displayStatus = "Prezent";
        } else if (statusList.includes("checkedin")) {
          displayStatus = "Checked-in";
        } else if (statusList.includes("confirmat")) {
          displayStatus = "Confirmat";
        }
      }

      return {
        ...event,
        isRegistered,
        isUserParticipant,
        additionalParticipantsCount,
        confirmedParticipants,
        confirmedSupporters,
        pendingParticipants,
        pendingSupporters,
        cancelledParticipants,
        cancelledSupporters,
        displayStatus,
      } satisfies HomeEvent;
    });
  });

  ngOnInit() {
  }

  registerForEvent(eventId: number) {
    if (this.authService.isAuthenticated()) {
      this.router.navigate(["/events", eventId, "register"]);
    } else {
      this.router.navigate(["/"], { queryParams: { returnUrl: `/events/${eventId}/register` } });
    }
  }

  goBack() {
    window.history.back();
  }
}
